import React, { useState, useEffect } from "react";
import "./DependencyAnalysis.css";

const DependencyAnalysisTab = ({ analysisData, sessionId }) => {
  const [activeTab, setActiveTab] = useState("visualization");

  // State for file-level graph visualization only
  const [graphView, setGraphView] = useState("file"); // Only 'file' view is supported
  const [selectedFile, setSelectedFile] = useState(null);
  const [graphHtml, setGraphHtml] = useState("");
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState("");


  // Fetch file-level graph when visualization tab is active
  useEffect(() => {
    if (activeTab === "visualization" && analysisData && sessionId) {
      // Only fetch file-level graphs
      fetchInteractiveGraph("file", null);
    }
  }, [activeTab, analysisData, sessionId]);

  // Listen for messages from iframe (file node clicks are disabled)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "FILE_NODE_CLICKED") {
        // Function-level graphs are disabled - do nothing
      } else if (event.data.type === "BACK_TO_FILES") {
        setSelectedFile(null);
        setGraphView("file");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const fetchInteractiveGraph = async (viewType, fileName = null) => {
    setGraphLoading(true);
    setGraphError("");

    // try {
    //   let url = `/api/sessions/${sessionId}/dependency-graph?graph_type=${viewType}`;
    //   if (viewType === 'function' && fileName) {
    //     url += `&file_name=${encodeURIComponent(fileName)}`;
    //   }

    try {
      let url = `${process.env.REACT_APP_API_URL || ""}/api/sessions/${sessionId}/dependency-graph?graph_type=${viewType}`;

      if (viewType === "function" && fileName) {
        url += `&file_name=${encodeURIComponent(fileName)}`;
      }


      const response = await fetch(url, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch dependency graph: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      setGraphHtml(data.html);
    } catch (err) {
      console.error("Error fetching dependency graph:", err);
      setGraphError(err.message);
      setGraphHtml("");
    } finally {
      setGraphLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };

  const renderMatrixTable = (
    matrixData,
    title,
    description,
    isBinary = true
  ) => {
    if (!matrixData) return null;

    // Handle both string and object formats
    if (typeof matrixData === "string") {
      return (
        <div className="matrix-display">
          <h3>{title}</h3>
          <p className="matrix-description">{description}</p>
          <div className="matrix-table">
            <pre>{matrixData}</pre>
          </div>
        </div>
      );
    }

    // Handle object format
    const {
      labels,
      matrix,
      type,
      total_dependencies,
      description: objDescription,
    } = matrixData;

    if (!labels || !matrix) {
      return (
        <div className="matrix-display">
          <h3>{title}</h3>
          <p className="matrix-description">{description}</p>
          <div className="matrix-table">
            <pre>{JSON.stringify(matrixData, null, 2)}</pre>
          </div>
        </div>
      );
    }

    return (
      <div className="matrix-display">
        <h3>{title}</h3>
        <p className="matrix-description">{description}</p>
        {objDescription && (
          <p className="matrix-description">{objDescription}</p>
        )}
        {total_dependencies && (
          <p className="matrix-description">
            Total Dependencies: {total_dependencies}
          </p>
        )}

        <div
          className="matrix-table-container"
          style={{ overflowX: "auto", maxWidth: "100%" }}
        >
          <table
            className="correlation-matrix-table"
            style={{ width: "max-content", minWidth: "100%" }}
          >
            <thead>
              <tr>
                <th
                  className="matrix-header"
                  style={{
                    fontSize: "14px",
                    padding: "8px 8px 8px 16px",
                    width: "auto",
                    position: "sticky",
                    left: 0,
                    zIndex: 20,
                    backgroundColor: "var(--border-light)",
                    boxShadow: "6px 0 16px rgba(0, 0, 0, 0.6)",
                  }}
                >
                  File
                </th>
                {labels.map((label, index) => (
                  <th
                    key={index}
                    className="matrix-header"
                    title={label}
                    style={{
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                      padding: "8px 12px",
                      width: "auto",
                      maxWidth: "none",
                      minWidth: "fit-content",
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td
                    className="matrix-label"
                    title={labels[rowIndex]}
                    style={{
                      fontSize: "14px",
                      padding: "8px 8px 8px 16px",
                      width: "auto",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      left: 0,
                      zIndex: 15,
                      backgroundColor: "var(--bg-gray-dark-1)",
                      boxShadow: "6px 0 16px rgba(0, 0, 0, 0.6)",
                      maxWidth: "none",
                      overflow: "visible",
                      textOverflow: "clip",
                    }}
                  >
                    {labels[rowIndex]}
                  </td>
                  {row.map((cell, colIndex) => (
                    <td
                      key={colIndex}
                      className={`matrix-cell ${
                        isBinary
                          ? cell
                            ? "matrix-cell-1"
                            : "matrix-cell-0"
                          : "matrix-cell-float"
                      }`}
                      title={`${labels[rowIndex]} → ${labels[colIndex]}: ${cell}`}
                    >
                      {isBinary ? (cell ? "1" : "0") : cell.toFixed(3)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="matrix-legend">
          <p>
            <strong>Legend:</strong>
          </p>
          {isBinary ? (
            <p>
              • <span className="legend-1">1</span> = Relationship exists
              (depends on / calls / related)
            </p>
          ) : (
            <p>
              • Values range from 0.0 (no correlation) to 1.0 (perfect
              correlation)
            </p>
          )}
          <p>
            • <span className="legend-0">0</span> = No relationship
          </p>
          <p>• Hover over cells for detailed information</p>
        </div>
      </div>
    );
  };

  const renderOverview = () => {
    if (!analysisData) return null;

    const metadata = analysisData.analysis_metadata || {};
    const metrics = analysisData.dependency_metrics || {};

    return (
      <div className="analysis-overview">
        <div className="overview-cards">
          <div className="overview-card">
            <h3>Files Analyzed</h3>
            <div className="card-value">{metadata.total_files || 0}</div>
            <div className="card-subtitle">PL/I source files</div>
          </div>

          <div className="overview-card">
            <h3>Functions/Procedures</h3>
            <div className="card-value">{metrics.total_nodes || 0}</div>
            <div className="card-subtitle">Total definitions found</div>
          </div>

          <div className="overview-card">
            <h3>Dependencies</h3>
            <div className="card-value">{metrics.total_edges || 0}</div>
            <div className="card-subtitle">Function calls detected</div>
          </div>

          <div className="overview-card">
            <h3>Analysis Time</h3>
            <div className="card-value">
              {formatDuration(metadata.analysis_duration || 0)}
            </div>
            <div className="card-subtitle">
              {analysisData.cache_info?.cached
                ? "Retrieved from cache"
                : "Fresh analysis"}
            </div>
          </div>
        </div>

        {metadata.file_list && (
          <div className="file-list-section">
            <h3>Analyzed Files</h3>
            <div className="file-list">
              {metadata.file_list.map((file, index) => (
                <div key={index} className="file-item">
                  <span className="file-icon">📄</span>
                  <span className="file-name">{file}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMetrics = () => {
    if (!analysisData?.dependency_metrics?.top_metrics) return null;

    const topMetrics = analysisData.dependency_metrics.top_metrics;

    return (
      <div className="metrics-section">
        <div className="metrics-grid">
          {topMetrics.top_fan_in && (
            <div className="metric-card">
              <h3>Most Called Functions (Fan-in)</h3>
              <div className="metric-list">
                {topMetrics.top_fan_in
                  .slice(0, 10)
                  .map(([func, count], index) => (
                    <div key={index} className="metric-item">
                      <span className="metric-name">{func}</span>
                      <span className="metric-value">{count} calls</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {topMetrics.top_fan_out && (
            <div className="metric-card">
              <h3>Functions Making Most Calls (Fan-out)</h3>
              <div className="metric-list">
                {topMetrics.top_fan_out
                  .slice(0, 10)
                  .map(([func, count], index) => (
                    <div key={index} className="metric-item">
                      <span className="metric-name">{func}</span>
                      <span className="metric-value">{count} calls</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {topMetrics.top_betweenness && (
            <div className="metric-card">
              <h3>Critical Connectors (Betweenness Centrality)</h3>
              <div className="metric-list">
                {topMetrics.top_betweenness
                  .slice(0, 10)
                  .map(([func, score], index) => (
                    <div key={index} className="metric-item">
                      <span className="metric-name">{func}</span>
                      <span className="metric-value">{score.toFixed(4)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {topMetrics.top_pagerank && (
            <div className="metric-card">
              <h3>Most Important Functions (PageRank)</h3>
              <div className="metric-list">
                {topMetrics.top_pagerank
                  .slice(0, 10)
                  .map(([func, score], index) => (
                    <div key={index} className="metric-item">
                      <span className="metric-name">{func}</span>
                      <span className="metric-value">{score.toFixed(4)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMatrices = () => {
    if (!analysisData?.visual_matrices) {
      return (
        <div className="matrix-section">
          <div className="no-data">
            <h3>Matrix data not available</h3>
            <p>Dependency and correlation matrices could not be generated.</p>
          </div>
        </div>
      );
    }

    const { visual_matrices, dependency_matrix } = analysisData;

    return (
      <div className="matrix-section">
        {/* Binary Correlation Matrix */}
        {renderMatrixTable(
          visual_matrices.correlation_binary,
          "📊 File Correlation Matrix (Binary)",
          "Shows relationships between files: 1 = Files are related (correlation > 0.5), 0 = Files are not related",
          true
        )}

        {/* File Dependency Matrix */}
        {renderMatrixTable(
          visual_matrices.file_dependency,
          "📁 File Dependency Matrix (Binary)",
          "Shows direct dependencies: 1 = File depends on another file, 0 = No dependency",
          true
        )}

        {/* Correlation Scores (Float) */}
        {renderMatrixTable(
          visual_matrices.correlation_scores,
          "🔢 File Correlation Scores (0.0 - 1.0)",
          "Detailed correlation scores between files based on shared functions, complexity, size, and variables",
          false
        )}

        {/* Matrix Summary */}
        {analysisData.matrix_summary_report && (
          <div className="matrix-display">
            <h3>📋 Matrix Summary Report</h3>
            <div className="matrix-table">
              <pre>{analysisData.matrix_summary_report}</pre>
            </div>
          </div>
        )}

        {/* Matrix Statistics */}
        {dependency_matrix && (
          <div className="matrix-stats">
            <h3>Matrix Statistics</h3>
            <div className="stats-grid">
              {dependency_matrix.file_dependency_matrix && (
                <div className="stat-card">
                  <h4>File Dependencies</h4>
                  <p>
                    Total:{" "}
                    {
                      dependency_matrix.file_dependency_matrix
                        .total_dependencies
                    }
                  </p>
                  <p>
                    Files:{" "}
                    {dependency_matrix.file_dependency_matrix.file_names
                      ?.length || 0}
                  </p>
                </div>
              )}
              {dependency_matrix.function_dependency_matrix && (
                <div className="stat-card">
                  <h4>Function Calls</h4>
                  <p>
                    Total:{" "}
                    {dependency_matrix.function_dependency_matrix.total_calls}
                  </p>
                  <p>
                    Functions:{" "}
                    {dependency_matrix.function_dependency_matrix.function_names
                      ?.length || 0}
                  </p>
                </div>
              )}
              {dependency_matrix.correlation_matrix && (
                <div className="stat-card">
                  <h4>Correlation</h4>
                  <p>
                    Average:{" "}
                    {dependency_matrix.correlation_matrix.average_correlation?.toFixed(
                      3
                    ) || "N/A"}
                  </p>
                  <p>Range: 0.0 - 1.0</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderVisualization = () => {
    if (!analysisData) {
      return (
        <div className="visualization-section">
          <div className="no-data">
            <h3>No analysis data available</h3>
            <p>Run dependency analysis first to view the interactive graph.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="visualization-section">
        <div className="visualization-header">
          <h3>📁 File-Level Dependencies</h3>
          <p className="graph-description">Shows file-to-file relationships.</p>
        </div>

        {graphLoading && (
          <div className="kg-loading">
            <div className="loading-spinner"></div>
            <p>Generating interactive dependency graph...</p>
          </div>
        )}

        {graphError && (
          <div className="kg-error">
            <div className="error-icon">⚠️</div>
            <h4>Error Loading Graph</h4>
            <p>{graphError}</p>
            <button
              className="retry-button"
              onClick={() => fetchInteractiveGraph(graphView, selectedFile)}
            >
              Retry
            </button>
          </div>
        )}

        {!graphLoading && !graphError && graphHtml && (
          <div className="visualization-container">
            <iframe
              className="dependency-graph-iframe"
              srcDoc={graphHtml}
              title="Interactive Dependency Graph"
              style={{
                width: "100%",
                height: "800px",
                border: "none",
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            />
          </div>
        )}

        {!graphLoading && !graphError && !graphHtml && (
          <div className="no-data">
            <h4>No Graph Data Available</h4>
            <p>Unable to generate dependency graph visualization.</p>
          </div>
        )}

        <div className="visualization-legend">
          {graphView === "file" ? (
            <>
              <p>
                <strong>📁 File View:</strong> Each node represents a PL/I file
              </p>
              <p>
                <strong>Click a file:</strong> View functions within that file
              </p>
              <p>
                <strong>Edge Width:</strong> Number of cross-file function calls
              </p>
              <p>
                <strong>Color:</strong> File complexity (Green=Low,
                Orange=Medium, Red=High)
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>⚙️ Function View:</strong> Functions within{" "}
                {selectedFile}
              </p>
              <p>
                <strong>Solid Lines:</strong> Internal function calls
              </p>
              <p>
                <strong>Dashed Lines:</strong> External calls to other files
              </p>
              <p>
                <strong>Color:</strong> Function complexity (Blue=Low,
                Orange=Medium, Red=High)
              </p>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderFileDetails = () => {
    if (!analysisData?.detailed_file_info) {
      return (
        <div className="file-details-section">
          <div className="no-data">
            <h3>File details not available</h3>
            <p>Detailed file information could not be loaded.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="file-details-section">
        <h3>📁 File-by-File Analysis with Function Impacts</h3>
        <div className="file-details-grid">
          {Object.entries(analysisData.detailed_file_info).map(
            ([fileName, fileInfo]) => {
              // Get functions for this file
              const functionsInFile = fileInfo.functions || [];

              // Calculate aggregate stats (simplified)
              const functionsWithLogs = functionsInFile.filter(
                (func) => func.has_logs
              ).length;
              const functionsWithLogic = functionsInFile.filter(
                (func) => func.has_logic
              ).length;

              return (
                <div key={fileName} className="file-detail-card enhanced">
                  <div className="file-card-header">
                    <h4>{fileName}</h4>
                  </div>

                  <div className="file-stats">
                    <div className="stat-item">
                      <span className="stat-label">Lines of Code:</span>
                      <span className="stat-value">
                        {fileInfo.lines_of_code || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="stat-item">
                    <details className="function-details">
                      <summary className="stat-label">
                        Total Functions ({functionsInFile.length})
                      </summary>
                      <div className="function-list">
                        {functionsInFile.map((func, index) => (
                          <div
                            key={index}
                            className="function-item all-function"
                          >
                            • {func.name}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    );
  };

  if (!analysisData) {
    return (
      <div className="dependency-analysis-tab">
        <div className="no-analysis">
          <h3>No Dependency Analysis Data</h3>
          <p>Please run dependency analysis first.</p>
          <div
            style={{
              marginTop: "20px",
              padding: "10px",
              background: "#2d2d2d",
              borderRadius: "4px",
              border: "1px solid #3a3a3a",
            }}
          >
            <strong style={{ color: "#ffffff" }}>Debug Info:</strong>
            <pre
              style={{ fontSize: "12px", marginTop: "10px", color: "#e0e0e0" }}
            >
              {JSON.stringify({ analysisData, sessionId }, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  // Check if analysisData has the expected structure
  if (
    !analysisData.analysis_metadata &&
    !analysisData.dependency_metrics &&
    !analysisData.file_dependencies
  ) {
    // Check if it's an authentication issue
    const isAuthError =
      analysisData.session_id && analysisData.analysis_results === null;

    return (
      <div className="dependency-analysis-tab">
        <div className="no-analysis">
          {isAuthError ? (
            <>
              <h3>🔒 Authentication Required</h3>
              <p style={{ fontSize: "16px", marginTop: "16px" }}>
                You must be <strong>logged in</strong> to run dependency
                analysis.
              </p>
              <div
                style={{
                  marginTop: "24px",
                  padding: "16px",
                  background: "#2d2d2d",
                  border: "1px solid #4da6ff",
                  borderRadius: "8px",
                }}
              >
                <strong style={{ color: "#ffffff" }}>Steps to proceed:</strong>
                <ol
                  style={{
                    marginTop: "12px",
                    textAlign: "left",
                    paddingLeft: "20px",
                    color: "#e0e0e0",
                  }}
                >
                  <li style={{ marginBottom: "8px" }}>
                    Click the <strong>Login</strong> button in the top-right
                    corner
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    Enter your credentials
                  </li>
                  <li style={{ marginBottom: "8px" }}>Close this tab</li>
                  <li>
                    Click <strong>📊 Dependency Analysis</strong> button again
                  </li>
                </ol>
              </div>
            </>
          ) : (
            <>
              <h3>Invalid Analysis Data Structure</h3>
              <p>The analysis data doesn't have the expected structure.</p>
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  background: "#2d2d2d",
                  border: "1px solid #3a3a3a",
                  borderRadius: "4px",
                }}
              >
                <strong style={{ color: "#ffffff" }}>
                  Received Data Structure:
                </strong>
                <pre
                  style={{
                    fontSize: "12px",
                    marginTop: "10px",
                    maxHeight: "300px",
                    overflow: "auto",
                    color: "#e0e0e0",
                  }}
                >
                  {JSON.stringify(analysisData, null, 2)}
                </pre>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dependency-analysis-tab">
      <div className="analysis-header">
        <h2>📊 Dependency Analysis</h2>
      </div>

      <div className="analysis-tabs">
        <button
          className={`tab-button ${
            activeTab === "visualization" ? "active" : ""
          }`}
          onClick={() => setActiveTab("visualization")}
          style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
        >
          <img
            src="/graph.svg"
            alt="Graph Icon"
            className="tab-icon"
            style={{
              width: "18px",
              height: "18px",
              display: "inline-block",
              verticalAlign: "middle",
              filter: activeTab === "visualization" ? "invert(1)" : "none",
            }}
          />
          Graph
        </button>
        <button
          className={`tab-button ${activeTab === "matrices" ? "active" : ""}`}
          onClick={() => setActiveTab("matrices")}
          style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
        >
          <img
            src="/table.svg"
            alt="Table Icon"
            className="tab-icon"
            style={{
              width: "18px",
              height: "18px",
              display: "inline-block",
              verticalAlign: "middle",
              filter: activeTab === "matrices" ? "invert(1)" : "none",
            }}
          />
          Matrices
        </button>
        <button
          className={`tab-button ${activeTab === "files" ? "active" : ""}`}
          onClick={() => setActiveTab("files")}
          style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
        >
          <img
            src="/file.svg"
            alt="File Icon"
            className="tab-icon"
            style={{
              width: "18px",
              height: "18px",
              display: "inline-block",
              verticalAlign: "middle",
              filter: activeTab === "files" ? "invert(1)" : "none",
            }}
          />
          File Details
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "visualization" && renderVisualization()}
        {activeTab === "matrices" && renderMatrices()}
        {activeTab === "files" && renderFileDetails()}
      </div>
    </div>
  );
};

export default DependencyAnalysisTab;
